import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { AlertTriangle, Info, ShieldCheck } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useActiveAddons } from "@/hooks/useActiveAddons";
import { useAuth } from "@/hooks/useAuth";

const ROLE_ADDON_MAP: Record<string, string> = {
  catering: "Catering Dashboard",
  assets: "Assets Dashboard",
  communications: "Communications Dashboard",
  community: "Community Dashboard",
  school_liaison: "School Liaison Dashboard",
  shop: "Shop",
};

interface EditMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: any;
  onSuccess: () => void;
  showForumBan?: boolean;
  currentUserId?: string;
  currentAdminCount?: number;
  ptaId?: string;
}

const ROLE_DESCRIPTIONS = {
  chair: "Leads the PTA, chairs meetings, and oversees all operations",
  treasurer: "Manages finances, budgets, and financial reporting",
  trustee: "Provides governance oversight and strategic guidance",
  secretary: "Records minutes, manages communications, and handles admin",
  catering: "Organizes refreshments and catering for PTA events",
  communications: "Manages newsletters, social media, and public relations",
  community: "Builds relationships with parents and local community",
  assets: "Manages PTA equipment, inventory, and physical resources",
  events: "Plans and coordinates PTA fundraising events",
  school_liaison: "Coordinates with school staff and manages school-PTA relations",
  shop: "Manages the PTA shop operations and inventory",
  occasional_helper: "Volunteers for specific events when available",
  custom: "Custom role with specific responsibilities defined by PTA",
};

const ROLES_WITH_DASHBOARD: Record<string, boolean> = {
  chair: true,
  treasurer: true,
  secretary: true,
  trustee: true,
  catering: true,
  communications: true,
  school_liaison: true,
  assets: true,
  community: true,
  shop: true,
  events: true,
};

const DBS_TYPES = [
  { value: "basic", label: "Basic" },
  { value: "standard", label: "Standard" },
  { value: "enhanced", label: "Enhanced" },
  { value: "enhanced_barred", label: "Enhanced with Barred List" },
];

export function EditMemberDialog({ open, onOpenChange, member, onSuccess, showForumBan = false, currentUserId, currentAdminCount = 0, ptaId }: EditMemberDialogProps) {
  const { subscription } = useAuth();
  const { hasAddon } = useActiveAddons();
  const isStandardPlan = subscription?.plan === 'standard';

  const isRoleVisible = (roleKey: string, currentlyAssigned: boolean): boolean => {
    if (currentlyAssigned) return true; // always show already-assigned roles
    if (isStandardPlan) return true;
    const addonName = ROLE_ADDON_MAP[roleKey];
    if (!addonName) return true;
    return hasAddon(addonName);
  };
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mainRoles, setMainRoles] = useState({
    chair: false,
    treasurer: false,
    trustee: false,
    secretary: false,
  });
  const [additionalRoles, setAdditionalRoles] = useState({
    catering: false,
    communications: false,
    community: false,
    assets: false,
    events: false,
    school_liaison: false,
    shop: false,
    occasional_helper: false,
    custom: false,
  });
  const [additionalAccess, setAdditionalAccess] = useState({
    documents: false,
    expenses: false,
  });
  const [documentPermission, setDocumentPermission] = useState<"read" | "edit">("read");
  const [forumAccessDisabled, setForumAccessDisabled] = useState(false);
  const [memberSince, setMemberSince] = useState("");
  const [loading, setLoading] = useState(false);
  
  // DBS Check fields
  const [dbsChecked, setDbsChecked] = useState(false);
  const [dbsCertificateNumber, setDbsCertificateNumber] = useState("");
  const [dbsIssueDate, setDbsIssueDate] = useState("");
  const [dbsExpiryDate, setDbsExpiryDate] = useState("");
  const [dbsType, setDbsType] = useState("enhanced");
  const [dbsNotes, setDbsNotes] = useState("");
  const [isPtaAdmin, setIsPtaAdmin] = useState(false);
  const [customRoleName, setCustomRoleName] = useState("");

  // Dashboard and Deputy state
  const [dashboardEnabled, setDashboardEnabled] = useState<Record<string, boolean>>({});
  const [deputyEnabled, setDeputyEnabled] = useState<Record<string, boolean>>({});
  const [deputyPrimary, setDeputyPrimary] = useState<Record<string, string>>({});
  const [ptaMembers, setPtaMembers] = useState<any[]>([]);

  useEffect(() => {
    if (member && open) {
      loadMemberData();
    }
  }, [member, open]);

  useEffect(() => {
    if (open && ptaId) {
      fetchPtaMembers();
    }
  }, [open, ptaId]);

  const fetchPtaMembers = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from("members")
      .select("id, full_name, email, role")
      .eq("pta_id", ptaId)
      .order("full_name");
    setPtaMembers(data || []);
  };

  const getRoleMembers = (role: string) => {
    return ptaMembers.filter(m => m.role && m.role.toLowerCase().includes(role.toLowerCase()));
  };

  const loadMemberData = async () => {
    // Set name and email from member
    setFullName(member.full_name || "");
    setEmail(member.email || "");
    
    // Only load roles if member has a user_id (is a registered user)
    if (member.user_id) {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', member.user_id);

      if (roles) {
        const roleNames = roles.map(r => r.role).filter(r => r !== 'site_admin');
        setMainRoles({
          chair: roleNames.includes('chair'),
          treasurer: roleNames.includes('treasurer'),
          trustee: roleNames.includes('trustee'),
          secretary: roleNames.includes('secretary'),
        });
        setAdditionalRoles({
          catering: roleNames.includes('catering'),
          communications: roleNames.includes('communications'),
          community: roleNames.includes('community'),
          assets: roleNames.includes('assets'),
          events: roleNames.includes('events'),
          school_liaison: roleNames.includes('school_liaison'),
          shop: roleNames.includes('shop'),
          occasional_helper: roleNames.includes('occasional_helper'),
          custom: roleNames.includes('custom'),
        });
      }
    } else {
      setMainRoles({ chair: false, treasurer: false, trustee: false, secretary: false });
      setAdditionalRoles({
        catering: false, communications: false, community: false, assets: false,
        events: false, school_liaison: false, shop: false, occasional_helper: false, custom: false,
      });
    }

    // Load additional access (includes dashboard_ entries)
    const { data: access } = await supabase
      .from('member_additional_access')
      .select('*')
      .eq('member_id', member.id);

    if (access) {
      setAdditionalAccess({
        documents: access.some(a => a.access_type === 'documents'),
        expenses: access.some(a => a.access_type === 'expenses'),
      });
      const docAccess = access.find(a => a.access_type === 'documents');
      if (docAccess?.permission_level) {
        setDocumentPermission(docAccess.permission_level as "read" | "edit");
      }

      // Load dashboard enabled state from dashboard_ prefixed entries
      const newDashboardEnabled: Record<string, boolean> = {};
      access.forEach(a => {
        if (a.access_type.startsWith('dashboard_')) {
          const role = a.access_type.replace('dashboard_', '');
          newDashboardEnabled[role] = a.dashboard_enabled !== false;
        }
      });
      setDashboardEnabled(newDashboardEnabled);
    }

    // Load deputy roles
    const { data: deputyData } = await supabase
      .from('deputy_roles')
      .select('*')
      .eq('deputy_member_id', member.id);

    if (deputyData && deputyData.length > 0) {
      const newDeputyEnabled: Record<string, boolean> = {};
      const newDeputyPrimary: Record<string, string> = {};
      deputyData.forEach(d => {
        newDeputyEnabled[d.role] = true;
        newDeputyPrimary[d.role] = d.primary_member_id;
      });
      setDeputyEnabled(newDeputyEnabled);
      setDeputyPrimary(newDeputyPrimary);
    } else {
      setDeputyEnabled({});
      setDeputyPrimary({});
    }

    // Load forum access status
    setForumAccessDisabled(member.forum_access_disabled || false);
    
    // Load member since date
    setMemberSince(member.member_since || "");
    
    // Load DBS check data
    setDbsChecked(member.dbs_checked === true);
    setDbsCertificateNumber(member.dbs_certificate_number || "");
    setDbsIssueDate(member.dbs_issue_date || "");
    setDbsExpiryDate(member.dbs_expiry_date || "");
    setDbsType(member.dbs_type || "enhanced");
    setDbsNotes(member.dbs_notes || "");
    setIsPtaAdmin(member.is_pta_admin === true);
    setCustomRoleName(member.custom_role_name || "");
  };

  const handleSubmit = async () => {
    setLoading(true);

    // Validate name and email
    if (!fullName.trim()) {
      toast({ title: "Validation Error", description: "Name is required", variant: "destructive" });
      setLoading(false);
      return;
    }

    if (!email.trim() || !email.includes('@')) {
      toast({ title: "Validation Error", description: "Valid email is required", variant: "destructive" });
      setLoading(false);
      return;
    }

    // Validate roles
    if (mainRoles.chair && mainRoles.treasurer) {
      toast({ title: "Invalid Selection", description: "A member cannot be both Chair and Treasurer", variant: "destructive" });
      setLoading(false);
      return;
    }

    // Validate custom role name
    if (additionalRoles.custom && !customRoleName.trim()) {
      toast({ title: "Validation Error", description: "Please enter a name for the custom role", variant: "destructive" });
      setLoading(false);
      return;
    }

    // Validate deputy selections
    const allSelectedRoles = [
      ...Object.entries(mainRoles).filter(([_, v]) => v).map(([k]) => k),
      ...Object.entries(additionalRoles).filter(([_, v]) => v).map(([k]) => k),
    ];
    for (const role of allSelectedRoles) {
      if (deputyEnabled[role] && !deputyPrimary[role]) {
        toast({ title: "Deputy Primary Required", description: `Please select a primary member for deputy ${role} role`, variant: "destructive" });
        setLoading(false);
        return;
      }
    }

    // Only update user_roles if member has a user_id (is a registered user)
    if (member.user_id) {
      // Delete existing roles EXCEPT site_admin
      await supabase.from('user_roles').delete().eq('user_id', member.user_id).neq('role', 'site_admin');

      // Add new roles
      const rolesToAdd = allSelectedRoles.filter(role => role !== 'site_admin');

      if (rolesToAdd.length > 0) {
        const roleInserts = rolesToAdd.map(role => ({
          user_id: member.user_id,
          role: role as any,
        }));
        
        const { error: insertError } = await supabase.from('user_roles').insert(roleInserts).select();
        
        if (insertError) {
          toast({ title: "Error saving roles", description: insertError.message, variant: "destructive" });
          setLoading(false);
          return;
        }
      }
    }

    // Update member record
    const allRoles = allSelectedRoles;
    await supabase
      .from('members')
      .update({
        full_name: fullName.trim(),
        email: email.trim(),
        role: allRoles.join(', '),
        is_pta_admin: isPtaAdmin,
        custom_role_name: additionalRoles.custom ? customRoleName.trim() : null,
        ...(showForumBan ? { forum_access_disabled: forumAccessDisabled } : {}),
        member_since: memberSince || null,
        dbs_checked: dbsChecked,
        dbs_certificate_number: dbsChecked ? dbsCertificateNumber || null : null,
        dbs_issue_date: dbsChecked && dbsIssueDate ? dbsIssueDate : null,
        dbs_expiry_date: dbsChecked && dbsExpiryDate ? dbsExpiryDate : null,
        dbs_type: dbsChecked ? dbsType : null,
        dbs_notes: dbsChecked ? dbsNotes || null : null,
      })
      .eq('id', member.id);

    // Delete existing access entries
    await supabase.from('member_additional_access').delete().eq('member_id', member.id);

    // Build new access entries
    const accessToAdd: any[] = [];

    // Documents & expenses
    if (additionalAccess.documents) {
      accessToAdd.push({
        member_id: member.id,
        access_type: 'documents',
        permission_level: documentPermission,
        dashboard_enabled: true,
      });
    }
    if (additionalAccess.expenses) {
      accessToAdd.push({
        member_id: member.id,
        access_type: 'expenses',
        permission_level: null,
        dashboard_enabled: true,
      });
    }

    // Dashboard settings for enabled roles
    for (const role of allSelectedRoles) {
      if (ROLES_WITH_DASHBOARD[role]) {
        accessToAdd.push({
          member_id: member.id,
          access_type: `dashboard_${role}`,
          permission_level: null,
          dashboard_enabled: dashboardEnabled[role] !== false,
        });
      }
    }

    if (accessToAdd.length > 0) {
      await supabase.from('member_additional_access').insert(accessToAdd);
    }

    // Delete existing deputy roles for this member, then re-insert
    await supabase.from('deputy_roles').delete().eq('deputy_member_id', member.id);

    for (const role of allSelectedRoles) {
      if (deputyEnabled[role] && deputyPrimary[role]) {
        await supabase.from('deputy_roles').insert({
          deputy_member_id: member.id,
          primary_member_id: deputyPrimary[role],
          role: role as any,
        });
      }
    }

    setLoading(false);
    toast({ title: "Success", description: "Member updated successfully" });
    onSuccess();
    onOpenChange(false);
  };

  const hasOccasionalHelper = additionalRoles.occasional_helper;
  const hasMainRole = Object.values(mainRoles).some((v) => v);
  const hasNoAccount = !member?.user_id;

  const renderDashboardDeputy = (role: string, isLeftColumn: boolean) => {
    if (!ROLES_WITH_DASHBOARD[role]) return null;
    const roleMembers = getRoleMembers(role);
    const canBeDeputy = roleMembers.length > 0 && role !== 'trustee';

    return (
      <div className="ml-4 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Dashboard</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side={isLeftColumn ? "right" : "left"} align="start" sideOffset={8}>
                <p className="text-sm">Enable or disable dashboard access for this role</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Switch
            checked={dashboardEnabled[role] !== false}
            onCheckedChange={checked => setDashboardEnabled(prev => ({ ...prev, [role]: checked }))}
          />
        </div>

        {role !== 'trustee' && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Deputy</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent side={isLeftColumn ? "right" : "left"} align="start" sideOffset={8}>
                    <p className="text-sm">Deputy shares dashboard data with the primary role holder</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Switch
                checked={deputyEnabled[role] || false}
                onCheckedChange={checked => setDeputyEnabled(prev => ({ ...prev, [role]: checked }))}
                disabled={!canBeDeputy}
              />
            </div>

            {deputyEnabled[role] && canBeDeputy && (
              <Select
                value={deputyPrimary[role]}
                onValueChange={value => setDeputyPrimary(prev => ({ ...prev, [role]: value }))}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select primary" />
                </SelectTrigger>
                <SelectContent>
                  {roleMembers.filter(m => m.id !== member?.id).map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
          <DialogDescription>Update roles and permissions for {member?.full_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* Member Information */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Member Information</h3>
            </div>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="edit-full-name">Full Name</Label>
                <Input id="edit-full-name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Enter full name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter email address" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-member-since">Member Since</Label>
                <Input id="edit-member-since" type="date" value={memberSince} onChange={(e) => setMemberSince(e.target.value)} />
              </div>
            </div>
          </div>

          <Separator />

          {/* No Account Warning */}
          {hasNoAccount && (
            <Alert variant="destructive" className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                This member hasn't created an account yet. Roles can only be assigned once they've signed up and linked their account.
              </AlertDescription>
            </Alert>
          )}

          {/* Main Roles */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Main Roles</h3>
              <p className="text-xs text-muted-foreground mb-3">Chair and Treasurer cannot be on same member</p>
            </div>
            <TooltipProvider>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(mainRoles).map(([role, value], index) => {
                  const tooltipSide = index % 2 === 0 ? "right" : "left";
                  const isLeftColumn = index % 2 === 0;
                  return (
                    <div key={role} className="space-y-2 p-3 border border-border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`edit-${role}`} className="cursor-pointer capitalize">{role}</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side={tooltipSide as any} align="start" className="max-w-xs z-[9999] bg-popover" sideOffset={8} collisionPadding={40}>
                              <p className="text-sm">{ROLE_DESCRIPTIONS[role as keyof typeof ROLE_DESCRIPTIONS]}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Switch
                          id={`edit-${role}`}
                          checked={value}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              if (role === 'chair') {
                                setMainRoles((prev) => ({ ...prev, chair: true, treasurer: false }));
                              } else if (role === 'treasurer') {
                                setMainRoles((prev) => ({ ...prev, treasurer: true, chair: false }));
                              } else {
                                setMainRoles((prev) => ({ ...prev, [role]: checked }));
                              }
                            } else {
                              setMainRoles((prev) => ({ ...prev, [role]: checked }));
                            }
                          }}
                          disabled={hasOccasionalHelper || hasNoAccount}
                        />
                      </div>
                      {value && renderDashboardDeputy(role, isLeftColumn)}
                    </div>
                  );
                })}
              </div>
            </TooltipProvider>
          </div>

          <Separator />

          {/* Additional Roles */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Additional Roles</h3>
              <p className="text-xs text-muted-foreground mb-3">Occasional helpers cannot have main roles</p>
            </div>
            <TooltipProvider>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(additionalRoles)
                  .filter(([role, value]) => isRoleVisible(role, value))
                  .map(([role, value], index) => {
                  const tooltipSide = index % 2 === 0 ? "right" : "left";
                  const isLeftColumn = index % 2 === 0;
                  return (
                    <div key={role} className="space-y-2 p-3 border border-border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`edit-${role}`} className="cursor-pointer capitalize">{role.replace("_", " ")}</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side={tooltipSide as any} align="start" className="max-w-xs z-[9999] bg-popover" sideOffset={8} collisionPadding={40}>
                              <p className="text-sm">{ROLE_DESCRIPTIONS[role as keyof typeof ROLE_DESCRIPTIONS]}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Switch
                          id={`edit-${role}`}
                          checked={value}
                          onCheckedChange={(checked) => setAdditionalRoles((prev) => ({ ...prev, [role]: checked }))}
                          disabled={role === "occasional_helper" ? (hasMainRole || hasNoAccount) : (hasOccasionalHelper || hasNoAccount)}
                        />
                      </div>
                      {value && role === 'custom' && (
                        <div className="mt-2">
                          <Input
                            placeholder="Enter custom role name"
                            value={customRoleName}
                            onChange={(e) => setCustomRoleName(e.target.value)}
                          />
                        </div>
                      )}
                      {value && role !== 'occasional_helper' && role !== 'custom' && renderDashboardDeputy(role, isLeftColumn)}
                    </div>
                  );
                })}
              </div>
            </TooltipProvider>
          </div>

          <Separator />

          {/* Additional Access */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Additional Access</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="edit-documents" className="cursor-pointer">Documents</Label>
                  {additionalAccess.documents && (
                    <div className="mt-2 ml-4">
                      <Select value={documentPermission} onValueChange={(v: "read" | "edit") => setDocumentPermission(v)}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="read">Read Only</SelectItem>
                          <SelectItem value="edit">Edit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <Switch id="edit-documents" checked={additionalAccess.documents} onCheckedChange={(checked) => setAdditionalAccess((prev) => ({ ...prev, documents: checked }))} disabled={hasOccasionalHelper} />
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="edit-expenses" className="cursor-pointer">Expense Claims</Label>
                </div>
                <Switch id="edit-expenses" checked={additionalAccess.expenses} onCheckedChange={(checked) => setAdditionalAccess((prev) => ({ ...prev, expenses: checked }))} disabled={hasOccasionalHelper} />
              </div>
            </div>
          </div>

          <Separator />

          {/* DBS Check Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm">DBS Check</h3>
            </div>
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="dbs-checked">DBS Check Required</Label>
                <p className="text-xs text-muted-foreground">Record DBS check details for this member</p>
              </div>
              <Switch id="dbs-checked" checked={dbsChecked} onCheckedChange={setDbsChecked} />
            </div>
            
            {dbsChecked && (
              <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                <div className="space-y-2">
                  <Label htmlFor="dbs-type">DBS Type</Label>
                  <Select value={dbsType} onValueChange={setDbsType}>
                    <SelectTrigger><SelectValue placeholder="Select DBS type" /></SelectTrigger>
                    <SelectContent>
                      {DBS_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dbs-certificate">Certificate Number</Label>
                  <Input id="dbs-certificate" value={dbsCertificateNumber} onChange={(e) => setDbsCertificateNumber(e.target.value)} placeholder="e.g., 001234567890" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="dbs-issue-date">Valid From</Label>
                    <Input id="dbs-issue-date" type="date" value={dbsIssueDate} onChange={(e) => setDbsIssueDate(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dbs-expiry-date">Expiry Date</Label>
                    <Input id="dbs-expiry-date" type="date" value={dbsExpiryDate} onChange={(e) => setDbsExpiryDate(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dbs-notes">Notes</Label>
                  <Textarea id="dbs-notes" value={dbsNotes} onChange={(e) => setDbsNotes(e.target.value)} placeholder="Optional notes about this DBS check" rows={2} />
                </div>
              </div>
            )}
          </div>

          {showForumBan && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="font-semibold text-sm">Forum Access</h3>
                <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="forum-access">Disable Forum Access</Label>
                    <p className="text-xs text-muted-foreground">Ban this user from accessing the forum</p>
                  </div>
                  <Switch id="forum-access" checked={forumAccessDisabled} onCheckedChange={setForumAccessDisabled} />
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* PTA Admin Toggle */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">PTA Admin</h3>
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="pta-admin-toggle">Grant PTA Admin Access</Label>
                <p className="text-xs text-muted-foreground">
                  {hasNoAccount
                    ? "Member must have an account to be made admin"
                    : isPtaAdmin && member?.user_id === currentUserId
                    ? "You cannot remove your own admin access"
                    : !isPtaAdmin && currentAdminCount >= 3
                    ? "Maximum of 3 admins reached"
                    : "Allow this member to manage PTA settings"}
                </p>
              </div>
              <Switch
                id="pta-admin-toggle"
                checked={isPtaAdmin}
                onCheckedChange={setIsPtaAdmin}
                disabled={
                  hasNoAccount ||
                  (member?.user_id === currentUserId && isPtaAdmin) ||
                  (!isPtaAdmin && currentAdminCount >= 3)
                }
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1">{loading ? "Saving..." : "Save Changes"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
